import React from "react";
import { Link } from "gatsby";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import Footer from "@/components/Footer";
import Header from "@/components/Header";
import TeamPreview from "@/components/TeamPreview";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { BOOKING_URL, LINK_ATTRIBUTES } from "@/lib/config";
import {
    Heart,
    Shield,
    Users,
    Star,
    Phone,
    ArrowRight,
    CheckCircle,
    Award,
    
} from "lucide-react";
import { locationConfigs } from "@/data/locationData";
import GoogleMap from "@/components/GoogleMap";


const Y3SmilesDentalPage = () => {
    useScrollAnimation();

    const values = [
        {
            icon: Heart,
            title: "We Care About You",
            description: "We make sure you feel comfortable. Every treatment is made just for you and your needs."
        },
        {
            icon: Shield,
            title: "Safe & Clean",
            description: "We keep everything very clean and safe. We follow the best health and safety rules."
        },
        {
            icon: Users,
            title: "For All Ages",
            description: "We help everyone in your family, from young kids to grandparents. Everyone is welcome here."
        },
        {
            icon: Star,
            title: "Great Quality",
            description: "We use modern equipment and the latest dental methods to give you the best results."
        }
    ];

    const treatments = [
        "Comprehensive dental examinations",
        "Preventive care and hygiene treatments",
        "Restorative dentistry and fillings",
        "Emergency dental treatments",
        "Children's dentistry (CDBS eligible)",
        "Cosmetic dental treatments",
        "Oral surgery and extractions",
        "Periodontal therapy"
    ];

    const facilities = [
        {
            title: "Modern Equipment",
            description: "State-of-the-art dental technology for precise diagnosis and treatment"
        },
        {
            title: "Comfortable Environment",
            description: "Relaxing clinic design to reduce anxiety and support patient comfort"
        },
        {
            title: "Digital Systems",
            description: "Easy booking, clear records, and simple treatment planning"
        },
        {
            title: "Accessible Location",
            description: "Convenient Broadmeadows location with easy parking"
        }
    ];

    return (
        <div className="min-h-screen flex flex-col">
    <SEO title="Our Dental Clinic in Broadmeadows | Y3 Smiles Dental" description="Y3 Smiles Dental in Broadmeadows — a modern clinic providing family, cosmetic & emergency care for Melbourne's northern suburbs." image="/DR%20Fatima%20Headshot%20New.png?v=20260421" canonicalUrl="/about/" />
            <Header />
            <main className="flex-grow">
                {/* Hero Section with Image Background */}
                <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden pt-20">
                    <div className="absolute inset-0 w-full h-full">
                        <picture>
                            <source srcSet="/clinic-pics/medium/Y3 - Photo-99.webp" type="image/webp" media="(max-width: 1280px)" />
                            <source srcSet="/clinic-pics/medium/Y3 - Photo-99.jpg" media="(max-width: 1280px)" />
                            <source srcSet="/clinic-pics/large/Y3 - Photo-99.webp" type="image/webp" />
                            <img
                                src="/clinic-pics/large/Y3 - Photo-99.jpg"
                                alt="Y3 Smiles Dental clinic interior"
                                className="w-full h-full object-cover"
                                loading="eager"
                                decoding="async"
                            />
                        </picture>
                    </div>

                    <div className="absolute inset-0 bg-gradient-to-br from-white/90 via-white/80 to-white/70"></div>

                    <div className="relative z-10 container mx-auto px-6 text-center py-12">
                        <div className="max-w-4xl mx-auto">
                            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-neutral-800 mb-6">
                                About Y3 Smiles Dental
                            </h1>
                            <p className="text-xl text-neutral-800 mb-8 leading-relaxed">
                                Your friendly dental care team in Broadmeadows. We help every member of your family get healthy teeth and bright smiles.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <Button variant="booking" size="xl" asChild className="group">
                                    <a href={BOOKING_URL} {...LINK_ATTRIBUTES}>
                                        <Phone className="w-5 h-5 mr-2 group-hover:rotate-12 transition-transform" />
                                        BOOK APPOINTMENT
                                    </a>
                                </Button>
                                <Button variant="outline" size="xl" asChild>
                                    <Link to="/treatments">
                                        <ArrowRight className="w-5 h-5 mr-2" />
                                        View Our Treatments
                                    </Link>
                                </Button>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Our Story */}
                <section className="py-24 bg-background">
                    <div className="container mx-auto px-6">
                        <div className="max-w-5xl mx-auto">
                            <div className="text-center mb-12">
                                <h2 className="text-2xl font-semibold text-primary uppercase tracking-widest mb-4">
                                    Our Story
                                </h2>
                            </div>

                            <div className="space-y-6 text-lg leading-relaxed">
                                <p className="text-neutral-800">
                                    At Y3 Smiles Dental, we believe healthy teeth help you feel happy and confident. We're a family dental clinic in Broadmeadows that provides quality care in a friendly, comfortable space. <strong>Healthy smiles, healthy lives — it begins with you</strong>.
                                </p>
                                <p className="text-neutral-800">
                                    We help with all types of dental care: regular check-ups, cleanings, fillings, kids' dentistry, teeth whitening, veneers, braces, and emergency dental work. Whatever you need, our team is here to help your whole family.
                                </p>
                                <p className="text-neutral-800">
                                    We listen to you and explain everything clearly. We want you to feel comfortable and relaxed during your visit. Your health and your smile are what matter most to us.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Dr Fatima Kurnaz Bio */}
                <section className="py-16 bg-neutral-50">
                    <div className="container mx-auto px-6">
                        <div className="max-w-4xl mx-auto">
                            <h2 className="text-3xl md:text-4xl font-bold text-neutral-800 mb-6">
                                Meet Dr Fatima Kurnaz
                            </h2>
                            <p className="text-lg text-neutral-800 mb-4 leading-relaxed">
                                Dr Fatima Kurnaz (DDS) is the principal dentist at Y3 Smiles Dental and the heart behind everything we do at the clinic. After completing her Doctor of Dental Surgery in 2014, Dr Kurnaz has spent over 11 years practising dentistry across a range of clinical settings throughout Australia — from busy metropolitan practices to community health environments — building a breadth of experience that is both clinically strong and deeply people-centred.
                            </p>
                            <p className="text-lg text-neutral-800 mb-4 leading-relaxed">
                                Dr Kurnaz has worked across general, family, and preventive dentistry, developing a genuine passion for making dental care feel safe, accessible, and positive for every patient who walks through the door — especially those who may have had difficult experiences with dental visits in the past. Her approach is calm, thorough, and unhurried, because she believes that when patients feel heard and comfortable, better health outcomes follow naturally.
                            </p>
                            <p className="text-lg text-neutral-800 mb-4 leading-relaxed">
                                Broadmeadows is not just where Dr Kurnaz practises — it is the community she is proud to serve. She has a special connection to the Turkish and Arabic-speaking families who have called this area home for generations, as well as the many families of former Ford and automotive industry workers whose roots run deep in Broadmeadows, Coolaroo, Meadow Heights, Campbellfield, Dallas, and Jacana. She understands the values, the history, and the trust that these communities place in local health professionals — and she takes that responsibility seriously.
                            </p>
                            <p className="text-lg text-neutral-800 leading-relaxed">
                                Dr Kurnaz is registered with the Dental Board of Australia and is a proud member of the Australian Dental Association (ADA). She is committed to ongoing professional development, staying current with modern techniques and evidence-based care to ensure every patient receives treatment they can feel confident in.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Clinic Values */}
                <section className="py-16 bg-background">
                    <div className="container mx-auto px-6">
                        <div className="max-w-4xl mx-auto">
                            <h2 className="text-3xl md:text-4xl font-bold text-neutral-800 mb-6">
                                Our Commitment to You
                            </h2>
                            <p className="text-lg text-neutral-800 mb-4 leading-relaxed">
                                At Y3 Smiles Dental, you are not just a patient — you are part of our family, and we mean that wholeheartedly.
                            </p>
                            <p className="text-lg text-neutral-800 mb-4 leading-relaxed">
                                We know that visiting the dentist can feel daunting, and we want you to know that from the moment you walk in, you are in a safe, caring, and welcoming space. Every member of our team genuinely cares about your wellbeing — not just your teeth, but how you feel, how comfortable you are, and whether you leave with a smile that goes beyond the dental chair.
                            </p>
                            <p className="text-lg text-neutral-800 mb-4 leading-relaxed">
                                We take the time to sit with you, listen to your concerns, and explain every step of your care in plain language — because you deserve to understand exactly what is happening and to feel in control of your own health decisions. There is no rush here. Your questions are always welcome.
                            </p>
                            <p className="text-lg text-neutral-800 mb-4 leading-relaxed">
                                We invest in modern equipment and maintain the highest infection control standards, because your safety is non-negotiable. We follow all guidelines set by the Dental Board of Australia, so you can trust that the care you receive meets rigorous clinical standards — every single visit.
                            </p>
                            <p className="text-lg text-neutral-800 leading-relaxed">
                                But beyond the clinical side, what we care about most is you — your comfort, your confidence, and your long-term health. Whether you are bringing in your little ones for their first check-up, supporting an elderly parent, or finally taking that step to address something you have been putting off — we are here for you, and we are honoured to be your dental home.
                            </p>
                        </div>
                    </div>
                </section>

                <TeamPreview className="bg-neutral-50" />

                {/* Our Values */}
                <section className="py-24 bg-neutral-50">
                    <div className="container mx-auto px-6">
                        <div className="max-w-6xl mx-auto">
                            <div className="text-center mb-16">
                                <h2 className="text-4xl md:text-5xl font-bold text-neutral-800 mb-6">
                                    Our Core Values
                                </h2>
                                <p className="text-xl text-neutral-800 max-w-3xl mx-auto">
                                    Our commitment to exceptional care is built on these principles that guide everything we do.
                                </p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                {values.map((value, index) => {
                                    const IconComponent = value.icon;
                                    return (
                                        <Card key={index} className="group bg-white hover:shadow-xl transition-all duration-300 border border-primary/5 hover:border-primary/20 fade-in-section" style={{ transitionDelay: `${index * 150}ms` }}>
                                            <CardContent className="pt-8 pb-8 px-8">
                                                <div className="flex items-start space-x-4">
                                                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/10 to-secondary/10 flex items-center justify-center group-hover:scale-110 transition-transform mt-1">
                                                        <IconComponent className="w-8 h-8 text-primary" />
                                                    </div>
                                                    <div className="flex-1 pt-3">
                                                        <h4 className="text-xl font-bold text-neutral-800 mb-3 group-hover:text-primary transition-colors">
                                                            {value.title}
                                                        </h4>
                                                        <p className="text-neutral-800 leading-relaxed">
                                                            {value.description}
                                                        </p>
                                                    </div>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </section>

                {/* Services Overview */}
                <section className="py-24 bg-background">
                    <div className="container mx-auto px-6">
                        <div className="max-w-4xl mx-auto">
                            <div className="text-center mb-16">
                                <h2 className="text-4xl md:text-5xl font-bold text-neutral-800 mb-6">
                                    Comprehensive Dental Treatments
                                </h2>
                                <p className="text-lg text-neutral-800">
                                    We offer a full range of dental treatments to meet all your family's oral health needs.
                                </p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12">
                                {treatments.map((service, index) => (
                                    <div key={index} className="flex items-center space-x-3 p-4 bg-white rounded-lg shadow-soft hover:shadow-medium transition-smooth">
                                        <CheckCircle className="w-6 h-6 text-primary flex-shrink-0" />
                                        <span className="text-neutral-700 font-medium">{service}</span>
                                    </div>
                                ))}
                            </div>

                            <div className="text-center">
                                <Button variant="booking" size="lg" asChild className="group">
                                    <Link to="/treatments">
                                        View All Treatments
                                        <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                                    </Link>
                                </Button>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Facilities */}
                <section className="py-24 bg-neutral-50">
                    <div className="container mx-auto px-6">
                        <div className="max-w-6xl mx-auto">
                            <div className="text-center mb-16">
                                <h2 className="text-4xl md:text-5xl font-bold text-neutral-800 mb-6">
                                    Modern Facilities
                                </h2>
                                <p className="text-lg text-neutral-800 max-w-3xl mx-auto">
                                    Our clinic is designed with patient comfort and modern dental care in mind.
                                </p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                {facilities.map((facility, index) => (
                                    <Card key={index} className="group hover:shadow-xl transition-all duration-300 border border-primary/5 hover:border-primary/20 fade-in-section" style={{ transitionDelay: `${index * 150}ms` }}>
                                        <CardContent className="pt-10 pb-8 px-8 text-center">
                                            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/10 to-secondary/10 flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                                                <Award className="w-8 h-8 text-primary" />
                                            </div>
                                            <h3 className="text-xl font-bold text-neutral-800 mb-3 group-hover:text-primary transition-colors">
                                                {facility.title}
                                            </h3>
                                            <p className="text-neutral-800 leading-relaxed">
                                                {facility.description}
                                            </p>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>

                            <div className="text-center mt-12">
                                <Button variant="outline" size="lg" asChild>
                                    <Link to="/about/clinic-photos">
                                        View Our Clinic Photos
                                        <ArrowRight className="w-5 h-5 ml-2" />
                                    </Link>
                                </Button>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Internal Linking Block */}
                <section className="py-12 bg-neutral-50">
                    <div className="container mx-auto px-6">
                        <div className="max-w-4xl mx-auto">
                            <h3 className="text-xl font-bold text-neutral-800 mb-4">Explore our services:</h3>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                {[
                                    { label: "Children's Dentistry", to: "/treatments/childrens-dentistry" },
                                    { label: "Emergency Dentist", to: "/treatments/emergency" },
                                    { label: "Dental Fillings", to: "/treatments/dental-fillings" },
                                    { label: "Root Canal Treatment", to: "/treatments/root-canal" },
                                    { label: "Crowns and Bridges", to: "/treatments/crown-bridge" },
                                    { label: "Dental Implants", to: "/treatments/dental-implants" },
                                    { label: "Wisdom Teeth Removal", to: "/treatments/wisdom-teeth" },
                                    { label: "All Treatments", to: "/treatments" },
                                ].map((link) => (
                                    <Link key={link.to} to={link.to} className="text-sm text-primary hover:underline font-medium py-1">
                                        {link.label}
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                {/* Location Info */}
                <GoogleMap
                    locationName="Broadmeadows"
                    locationData={locationConfigs.broadmeadows.locationData}
                    openingHours={locationConfigs.broadmeadows.openingHours}
                />
            </main>
            <Footer />
        </div>
    );
};

export default Y3SmilesDentalPage;